import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Card, CardMedia, Typography } from '@mui/material'
import useModals from 'src/Hooks/useModals'
import { makeStyles } from 'tss-react/mui'
import { useTypedSelector } from 'src/State/RootReducer'
import { Product } from 'src/Libs/shop/models/product.model'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { getImageUrlBySize } from 'src/Libs/shop/utils'
import { useDispatch } from 'react-redux'
import { removeSmartMeter } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import SmartMeterDefaultImage from 'src/Resources/Images/shop/SmartMeterDefaultImage.png'
import { ShopDownloadButton } from '../../Button'

interface SmartMeterCardProps {
  availableSmartMeters: Product[] | null
}

const useStyles = makeStyles()(theme => ({
  product: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: '265px',
    maxWidth: '225px',
    minHeight: '350px',
    flexShrink: 0,
    margin: theme.spacing(2),
    padding: 0,
    paddingBottom: theme.spacing(2),
    position: 'relative',
  },
  productImage: {
    margin: 0,
    padding: 0,
    objectFit: 'fill',
    width: '100%',
    maxWidth: '100%',
    height: '220px',
    marginBottom: theme.spacing(2),
    '&:hover': {
      cursor: 'pointer',
    },
  },
  productTitle: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  addButton: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  button: {
    padding: `${theme.spacing(3)} 0`,
  },
  documentButtonContainer: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}))

const SmartMeterCard: React.FC<SmartMeterCardProps> = ({ availableSmartMeters }) => {
  const dispatch = useDispatch()

  const { openShopSmartMeterModal, openShopMoreInfo } = useModals()

  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const configuratorItems = useTypedSelector(state => state.systemStorage.items)
  const components = configuratorItems.length > 0 ? configuratorItems[0].configuration.smartMeter : null
  const selectedSmartMeter = availableSmartMeters?.find(smartMeter => components?.id === smartMeter.id)
  const selectedSmartMeterId = selectedSmartMeter?.id ?? null
  const selectedSmartMeterImage = getImageUrlBySize(selectedSmartMeter?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
  const selectedSmartMeterBlankContract = selectedSmartMeter?.extensions?.blankContract

  const handleProductDetails = (productId: string | null): void => {
    if (productId) {
      return openShopMoreInfo({ productId: productId })
    }
    if (availableSmartMeters) {
      openShopSmartMeterModal({ smartMeters: availableSmartMeters, selectedSmartMeterId: productId })
    }
  }

  return (
    <>
      {availableSmartMeters && availableSmartMeters.length > 0 && (
        <Card className={classes.product}>
          <CardMedia
            onClick={() => {
              handleProductDetails(selectedSmartMeterId)
            }}
            className={classes.productImage}
            component="img"
            image={selectedSmartMeter ? selectedSmartMeterImage : SmartMeterDefaultImage}
            alt="Product"
          />
          {selectedSmartMeterBlankContract && (
            <Box className={classes.documentButtonContainer}>
              <ShopDownloadButton contractDetails={selectedSmartMeterBlankContract} />
            </Box>
          )}
          <Typography className={classes.productTitle}>{selectedSmartMeter?.translated?.name}</Typography>
          {selectedSmartMeterId ? (
            <Box className={classes.buttonContainer}>
              <Button
                className={classes.button}
                onClick={() => {
                  openShopSmartMeterModal({ smartMeters: availableSmartMeters, selectedSmartMeterId })
                }}
                color="primary"
                variant="contained"
                fullWidth
              >
                {t('shop.smart-meter.change-button-label')}
              </Button>
              <Button className={classes.button} onClick={() => dispatch(removeSmartMeter())} color="inherit" variant="contained" fullWidth>
                {t('shop.smart-meter-remove-button-label')}
              </Button>
            </Box>
          ) : (
            <>
              {t('shop.smart-meter.no-smart-meter-selected')}
              <Button
                className={classes.addButton}
                size="large"
                variant="contained"
                onClick={() => {
                  openShopSmartMeterModal({ smartMeters: availableSmartMeters, selectedSmartMeterId })
                }}
              >
                {t('shop.additional-component.add-smart-meter')}
              </Button>
            </>
          )}
        </Card>
      )}
    </>
  )
}

export default SmartMeterCard
